import React, { useRef } from "react";
import { CheckCircle2, Printer, X } from "lucide-react";
import { money, formatDate } from "../../lib/api";

// Printable payment receipt for a paid invoice.
export default function Receipt({ invoice, onClose }) {
  const ref = useRef(null);

  const print = () => {
    const html = ref.current?.innerHTML;
    const w = window.open("", "_blank", "width=720,height=900");
    if (!w) return;
    w.document.write(`<html><head><title>Receipt ${invoice.number}</title>
      <style>body{font-family:Georgia,serif;color:#1f2937;padding:32px;max-width:640px;margin:auto}
      h1{color:#1E4023}table{width:100%;border-collapse:collapse;font-family:Arial}
      td{padding:6px 0}.r{text-align:right}.tot{font-size:20px;font-weight:bold;color:#1E4023}
      hr{border:none;border-top:1px solid #e5e7eb;margin:14px 0}.muted{color:#6b7280}</style>
      </head><body>${html}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="receipt-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2 text-green-600 font-semibold">
            <CheckCircle2 className="h-5 w-5" /> Payment Receipt
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-navy" data-testid="receipt-close"><X className="h-5 w-5" /></button>
        </div>

        <div ref={ref} className="p-6">
          <h1 className="font-serif text-xl font-bold text-navy">Rehabilitated For Life SSM</h1>
          <p className="muted text-sm text-gray-500">Payment received — thank you!</p>

          <table className="w-full text-sm mt-4">
            <tbody>
              <tr><td className="muted text-gray-500">Invoice</td><td className="r text-right font-medium">{invoice.number}</td></tr>
              <tr><td className="muted text-gray-500">Billed to</td><td className="r text-right">{invoice.client_name}</td></tr>
              <tr><td className="muted text-gray-500">Date paid</td><td className="r text-right">{formatDate(invoice.paid_at || invoice.created_at)}</td></tr>
              <tr><td className="muted text-gray-500">Method</td><td className="r text-right">{invoice.payment_method || "PayPal"}</td></tr>
              {invoice.payment_id && <tr><td className="muted text-gray-500">Confirmation</td><td className="r text-right text-xs break-all">{invoice.payment_id}</td></tr>}
            </tbody>
          </table>

          <hr className="my-4 border-gray-100" />
          <table className="w-full text-sm">
            <tbody>
              {invoice.items?.map((it, i) => (
                <tr key={i}><td className="text-gray-600">{it.description} × {it.quantity}</td><td className="r text-right">{money(it.unit_price * it.quantity)}</td></tr>
              ))}
            </tbody>
          </table>
          <hr className="my-3 border-gray-100" />
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="muted text-gray-500">Subtotal</td><td className="r text-right">{money(invoice.subtotal)}</td></tr>
              <tr><td className="muted text-gray-500">Tax</td><td className="r text-right">{money(invoice.tax)}</td></tr>
              <tr><td className="tot pt-1.5 font-bold text-navy">Total Paid</td><td className="r text-right tot pt-1.5 font-serif text-lg font-bold text-navy">{money(invoice.total)}</td></tr>
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={print} className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="receipt-print">
            <Printer className="h-4 w-4" /> Print / Save PDF
          </button>
        </div>
      </div>
    </div>
  );
}
